<script>
	import { Loaders, CodeSnippet } from '@shopboxai/sb-portal-components';
	import EmbedCodeAccordion from '../components/EmbedCodeAccordion.svelte';
	import Accordion from '../components/Accordion.svelte';
	import { createEventDispatcher } from 'svelte';
	import dayjs from 'dayjs';

	export let currentClient;

	let disabled = 'disabled';
	const format = 'YYYY-MM-DD';
	const endDate = dayjs().format(format);
	const startDate = dayjs(endDate).subtract(7, 'day').format(format);

	const dispatch = createEventDispatcher();
	const redirect = () => {
		dispatch('redirect', '/dashboard');
	};

	const overviewData = async () => {
		const url = `https://sb-reports-api-jtdpxp3bfa-ew.a.run.app/reports_async/${currentClient}?report_name=_dash&start_date=${startDate}&end_date=${endDate}`;
		const res = await fetch(url);
		let data = await res.json();
		return data;
	};

	const appInserts = [
		{
			id: 1,
			title: 'Setup your Shopbox scripts',
			img: '/img/step-1.png',
			description: 'Embedding our plugin is easy - just follow the steps described here.',
			resourcesURL:
				'https://docs.shopbox.ai/docs/getting-started/shopbox-app-implementation#prerequisites',
			resourcesName: 'Setup guide',
			embedValue: `<script>
    document.addEventListener('DOMContentLoaded', () => {
      var sbid = '${currentClient}';
      var t = document.createElement("shopbox-app-v2"),
        s = document.createElement("script");
      t.setAttribute('cid', sbid); s.type = "text/javascript";
      s.async = !0; s.src = "https://widget.shopbox.ai/js/app.js";
      document.getElementsByTagName("body")[0].append(t);
      document.getElementsByTagName("head")[0].append(s);
    })
    </\script>
<script async src="https://grid.shopbox.ai/sbgrid.min.js"></\script>`
		}
	];

	const documentationData = {
		'Shopbox App':
			'Setting up Shopbox is easy. There are few simple steps to get you setup and different components you can enable. Follow the link to our docs to get started!'
	};

	const loaders = [
		{
			size: 3,
			height: '150px'
		},
		{
			size: 3,
			height: '150px'
		},
		{
			size: 3,
			height: '150px'
		},
		{
			size: 3,
			height: '150px'
		}
	];

	const formatNumber = (value, format) => {
		if (format == 'percent') {
			return `${Math.round(value * 100)}%`;
		} else {
			return value;
		}
	};
</script>

<h1>Welcome to the Shopbox Portal</h1>
<section class="overview-section">
	<div class="overview-section-header">
		<h2>Overview</h2>
		<a href="/" on:click|preventDefault={redirect}>
			<svg
				width="16"
				height="16"
				viewBox="0 0 16 16"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M12 13.3333V6.66667"
					stroke="#091EB5"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M8 13.3333V2.66667"
					stroke="#091EB5"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M4 13.3333V9.33334"
					stroke="#091EB5"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
			View reports</a
		>
	</div>
	{#await overviewData()}
		<Loaders items={loaders} />
	{:then data}
		<div class="overview-data-container">
			{#each Object.entries(data) as [key, val]}
				<div class="overview-card">
					<h3>{val['title']}</h3>
					<span>{formatNumber(val['value'], val['format'])}</span>
					<p class="overview-subtitle">{val['subtitle']}</p>
				</div>
			{/each}
		</div>
	{:catch error}
		<div class="overview-no-data">
			<p>No data available yet.</p>
		</div>
	{/await}
</section>

<section class="inserts-section">
	<h2>App inserts</h2>
	<div class="inserts-data-container">
		{#each appInserts as step}
			<div class="inserts-wrapper">
				<div class="inserts-card">
					<img src={step.img} alt="" width="120px" height="120px" />
					<div class="inserts-card-data">
						<h2>{step.title}</h2>
						<p class="inserts-subtitle">{step.description}</p>
						<a target="_blank" href={step.resourcesURL}>{step.resourcesName}</a>
					</div>
				</div>
				{#if step.id == 1}
					<EmbedCodeAccordion value={step.embedValue} />
				{:else}
					<EmbedCodeAccordion value="" {disabled} />
				{/if}
			</div>
		{/each}
	</div>
</section>

<section class="documentation-section">
	<div class="documentation-wrapper">
		<h2>Documentation</h2>
		<div class="documentation-inner">
			<h3>Getting started</h3>
			{#each Object.entries(documentationData) as entry}
				<Accordion {entry} />
			{/each}

			<a target="_blank" href="https://docs.shopbox.ai/"
				>See more in our Docs Portal <svg
					style="tran"
					width="12"
					height="12"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"><path d="M9 5l7 7-7 7" /></svg
				></a
			>
		</div>
	</div>
</section>

<style>
	.overview-section,
	.inserts-section {
		margin-bottom: 2em;
	}
	.overview-section-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
	.overview-section-header a {
		color: var(--h-c);
		position: relative;
	}
	.overview-section-header a svg {
		position: absolute;
		left: -20px;
	}
	.overview-section h2,
	.inserts-section h2,
	.documentation-section h2 {
		font-family: 'overpass';
		font-size: 1.4rem;
		font-weight: 600;
		color: var(--t-c);
		letter-spacing: 1.15px;
		margin: 1em 0;
	}

	.overview-data-container,
	.inserts-data-container,
	.documentation-section {
		display: grid;
		grid-template-columns: repeat(12, 1fr);
		gap: 1em;
	}
	.overview-no-data {
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 0 8px -2px rgb(24 39 75 / 12%), 0 4px 4px -2px rgb(24 39 75 / 16%);
		min-height: 140px;
	}

	.overview-card {
		display: grid;
		grid-column: span 3;
		grid-template-rows: 30px 1fr 20px;
		min-height: 140px;
		box-shadow: 0 0 8px -2px rgb(24 39 75 / 12%), 0 4px 4px -2px rgb(24 39 75 / 16%);
		padding: 1em;
	}
	.overview-card h3 {
		font-size: 1em;
	}
	.overview-card span {
		align-self: end;
		font-family: 'Chivo';
		font-weight: 900;
		font-size: 2.6em;
		color: var(--h-c);
	}
	.overview-subtitle {
		text-transform: capitalize;
		font-size: 14px;
		font-weight: 400;
		color: var(--t-c);
		align-self: end;
	}
	.inserts-section h2,
	.documentation-section h2 {
		font-size: 1.1em;
		margin-bottom: 0.5em;
	}
	.inserts-wrapper {
		grid-column: span 6;
		position: relative;
	}
	.inserts-card {
		background-color: var(--bg-c);
		border-radius: 4px;
		display: flex;
		padding: 1em;
	}

	.inserts-card img {
		scale: 1.5;
	}
	.inserts-card-data h2 {
		font-family: 'Overpass';
		font-weight: 400;
		font-size: 1em;
		letter-spacing: 1.5px;
		text-transform: uppercase;
		margin-bottom: 0.8em;
		color: var(--h-c);
	}

	.inserts-subtitle {
		font-size: 1em;
		color: var(--t-c-l);
		font-weight: 400;
		margin-bottom: 0.3em;
		letter-spacing: 0.1em;
	}
	.inserts-card-data a {
		font-size: 0.9em;
	}
	.documentation-wrapper {
		grid-column: span 6;
		color: var(--t-c);
	}
	.documentation-inner {
		border: 1px solid #d0d0d2;
		border-radius: 4px;
		padding: 1em;
	}
	.documentation-inner h3 {
		font-size: 1rem;
		margin-bottom: 1em;
	}
	.documentation-inner a {
		margin-top: 1em;
		display: block;
		text-align: center;
		color: var(--h-c);
	}

	@media (max-width: 1160px) {
		.overview-card {
			grid-column: span 6;
		}
		.inserts-wrapper,
		.documentation-wrapper {
			grid-column: span 12;
		}
	}
	@media (max-width: 650px) {
		.overview-card {
			grid-column: span 12;
		}
		.inserts-card img {
			display: none;
		}
	}
</style>
